import React from 'react';
import { render, act } from '@testing-library/react';
import PlaylistSlideshow from './playlist_slideshow';

// Slide preloading + retention (S3 spend work).
//
// A TV loops the same playlist for hours. Two failures used to cost real
// money and real quality:
//
//   1. No preload — each advance mounted a fresh <img src> and could show
//      a blank frame while the image downloaded.
//   2. No retention — the loop's second pass depended entirely on the
//      browser HTTP cache, which (before Cache-Control shipped) had no
//      freshness directive to work with.
//
// The fix must stay *bounded*: exactly one slide ahead, and a capped LRU.
// Prefetching the whole playlist would trade one problem for another.

document.documentElement.requestFullscreen = jest.fn().mockResolvedValue();
document.exitFullscreen = jest.fn().mockResolvedValue();
Object.defineProperty(document, 'fullscreenElement', { value: null, writable: true });

// Capture every `new Image()` the component creates, along with the src
// it was assigned. That is the observable side effect of a preload.
let createdImages;
const OriginalImage = global.Image;

function installImageSpy() {
    createdImages = [];
    global.Image = class FakeImage {
        constructor() {
            this._src = null;
            createdImages.push(this);
        }
        set src(value) {
            this._src = value;
        }
        get src() {
            return this._src;
        }
    };
}

function preloadedSrcs() {
    return createdImages.map((img) => img.src);
}

function makeItems(count) {
    return Array.from({ length: count }, (_, i) => ({
        billboard_media_item: { url: `https://example.com/${i + 1}.jpg` },
    }));
}

beforeEach(() => {
    installImageSpy();
    jest.useFakeTimers();
});

afterEach(() => {
    jest.useRealTimers();
    global.Image = OriginalImage;
});

// Advances the player by one slide: the fade-out timer fires at FADE_MS,
// which is when the index actually swaps.
function advance(container) {
    act(() => {
        // 10s slide interval (the default) then the 500ms fade.
        jest.advanceTimersByTime(10000);
    });
    act(() => {
        jest.advanceTimersByTime(500);
    });
    return container;
}

describe('PlaylistSlideshow — slide preloading', () => {
    it('preloads exactly one slide ahead on mount', () => {
        render(<PlaylistSlideshow items={makeItems(5)} onClose={jest.fn()} />);

        // Current slide + one ahead. Nothing further.
        expect(preloadedSrcs()).toEqual([
            'https://example.com/1.jpg',
            'https://example.com/2.jpg',
        ]);
    });

    it('does not preload the whole playlist', () => {
        render(<PlaylistSlideshow items={makeItems(20)} onClose={jest.fn()} />);
        expect(createdImages.length).toBe(2);
    });

    it('preloads the next slide after an advance', () => {
        const { container } = render(
            <PlaylistSlideshow items={makeItems(5)} onClose={jest.fn()} />
        );
        advance(container);

        // Slide 2 is now current (already warmed); slide 3 is the new
        // one-ahead. Slide 4 must not have been touched.
        expect(preloadedSrcs()).toContain('https://example.com/3.jpg');
        expect(preloadedSrcs()).not.toContain('https://example.com/4.jpg');
    });

    it('wraps the preload around the end of the playlist', () => {
        const { container } = render(
            <PlaylistSlideshow items={makeItems(2)} onClose={jest.fn()} />
        );
        advance(container);
        // On slide 2 of 2, one-ahead is slide 1 — already retained, so
        // no new Image is constructed for it.
        expect(createdImages.length).toBe(2);
    });

    it('renders the full-resolution url, not a preview derivative', () => {
        // Derivatives are ~480px admin-grid previews. Showing one on a
        // TV would be a visible quality regression.
        const items = [
            {
                billboard_media_item: {
                    url: 'https://example.com/full.jpg',
                    thumbnail_url: 'https://example.com/thumb.webp',
                    still_url: 'https://example.com/still.webp',
                },
            },
        ];
        const { container } = render(
            <PlaylistSlideshow items={items} onClose={jest.fn()} />
        );
        expect(container.querySelector('img').getAttribute('src')).toBe(
            'https://example.com/full.jpg'
        );
    });

    it('supports flat items that carry the url directly', () => {
        render(
            <PlaylistSlideshow
                items={[{ url: 'https://example.com/flat.jpg' }]}
                onClose={jest.fn()}
            />
        );
        expect(preloadedSrcs()).toContain('https://example.com/flat.jpg');
    });

    it('does nothing when the playlist is empty', () => {
        render(<PlaylistSlideshow items={[]} onClose={jest.fn()} />);
        expect(createdImages).toEqual([]);
    });
});

describe('PlaylistSlideshow — bounded retention', () => {
    it('does not re-request an already-retained slide on the second loop pass', () => {
        const { container } = render(
            <PlaylistSlideshow items={makeItems(3)} onClose={jest.fn()} />
        );
        // One full loop: 1 -> 2 -> 3 -> 1.
        advance(container);
        advance(container);
        advance(container);

        // Three distinct slides, three Image objects — the second pass
        // reused the retained ones rather than constructing new ones.
        expect(createdImages.length).toBe(3);
        expect(new Set(preloadedSrcs()).size).toBe(3);
    });

    it('caps the retained set and evicts the least recently used entry', () => {
        const items = makeItems(30);
        let instance;
        render(
            <PlaylistSlideshow
                ref={(ref) => { instance = ref; }}
                items={items}
                onClose={jest.fn()}
            />
        );
        for (let i = 0; i < 25; i += 1) {
            act(() => { jest.advanceTimersByTime(10000); });
            act(() => { jest.advanceTimersByTime(500); });
        }

        expect(instance._imageCache.size).toBeLessThanOrEqual(10);
        // The very first slide is long gone from the working set.
        expect(instance._imageCache.has('https://example.com/1.jpg')).toBe(false);
        // The slide currently on screen is retained.
        const currentUrl = items[instance.state.currentIndex].billboard_media_item.url;
        expect(instance._imageCache.has(currentUrl)).toBe(true);
    });

    it('releases retained bitmaps on unmount', () => {
        let instance;
        const { unmount } = render(
            <PlaylistSlideshow
                ref={(ref) => { instance = ref; }}
                items={makeItems(5)}
                onClose={jest.fn()}
            />
        );
        // Hold the Map itself — React nulls the ref callback on unmount.
        const cache = instance._imageCache;
        expect(cache.size).toBeGreaterThan(0);
        unmount();
        expect(cache.size).toBe(0);
    });
});
